import { isValidCurp, normalizeCurp } from './curp'
import { partirNombre } from './nombres'

// La plantilla pide las partes por separado: el apellido paterno deja de
// adivinarse. Los archivos que el club ya tenga siguen sirviendo -- el parser
// acepta las dos formas y parte `nombre_completo` con partirNombre().
const TEMPLATE_HEADERS = [
  'nombres', 'apellidos', 'curp', 'fecha_nacimiento', 'genero', 'numero',
  'nickname', 'telefono_jugador', 'tutor_nombres', 'tutor_apellidos',
  'tutor_telefono', 'email_tutor',
]

export const buildPlayersTemplateCsv = () => TEMPLATE_HEADERS.join(',') + '\n'

// Tokeniza CSV (comillas dobles, comas embebidas, CRLF/LF) a array de arrays.
const parseCsvRecords = (text) => {
  const records = []
  let row = []
  let field = ''
  let inQuotes = false
  const s = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++ } else { inQuotes = false }
      } else { field += c }
    } else if (c === '"') {
      inQuotes = true
    } else if (c === ',') {
      row.push(field); field = ''
    } else if (c === '\n') {
      row.push(field); records.push(row); row = []; field = ''
    } else {
      field += c
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); records.push(row) }
  return records
}

export const parsePlayersCsv = (text) => {
  const records = parseCsvRecords(text)
  if (records.length === 0) {
    return { rows: [], errors: [{ line: 0, message: 'El archivo esta vacio.' }] }
  }
  const headers = records[0].map((h) => h.trim())
  if (!headers.includes('nombres') && !headers.includes('nombre_completo')) {
    return { rows: [], errors: [{ line: 1, message: "Falta la columna 'nombres' (o 'nombre_completo')." }] }
  }
  const rows = []
  for (let i = 1; i < records.length; i++) {
    const cells = records[i]
    if (cells.length === 1 && cells[0].trim() === '') continue
    const row = {}
    headers.forEach((h, idx) => { row[h] = (cells[idx] ?? '').trim() })
    row.__line = i + 1
    rows.push(row)
  }
  return { rows, errors: [] }
}

const GENEROS = ['Varonil', 'Femenil']

// Acepta AAAA-MM-DD o DD/MM/AAAA (formato comun de hojas mexicanas/AppSheet;
// tambien admite separadores . o -). Devuelve '' si viene vacia, la fecha en
// ISO (AAAA-MM-DD) si es valida, o null si es invalida.
const normalizeFecha = (raw) => {
  const s = String(raw || '').trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return Number.isNaN(Date.parse(s)) ? null : s
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (m) {
    const dd = m[1].padStart(2, '0')
    const mm = m[2].padStart(2, '0')
    const yyyy = m[3]
    if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return null
    const iso = `${yyyy}-${mm}-${dd}`
    return Number.isNaN(Date.parse(iso)) ? null : iso
  }
  return null
}

export const normalizeName = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

// Las partes capturadas mandan sobre el texto completo: si el archivo trae
// `nombres`/`apellidos` no se re-adivina nada. Solo cuando vienen vacías se parte
// el `nombre_completo` de los archivos viejos.
//
// El texto original es el último recurso: partirNombre() devuelve ('','') cuando el
// campo era pura aclaración ("Mamá"), y sin este respaldo una fila que hoy se
// importa dejaría de hacerlo.
const partesDe = (nombres, apellidos, completo) => {
  const n = String(nombres || '').trim()
  const a = String(apellidos || '').trim()
  const partes = (n || a) ? { nombres: n, apellidos: a } : partirNombre(completo)
  const nombre = `${partes.nombres} ${partes.apellidos}`.trim() || String(completo || '').trim()
  return { ...partes, nombre }
}

export const validateRow = (row) => {
  const jugador = partesDe(row.nombres, row.apellidos, row.nombre_completo)
  const nombre = jugador.nombre
  if (!nombre) return { valid: false, reason: 'Falta el nombre.' }

  const tutor = partesDe(row.tutor_nombres, row.tutor_apellidos, row.tutor_nombre)

  const curp = normalizeCurp(row.curp)
  if (curp && !isValidCurp(curp)) return { valid: false, reason: 'CURP invalida (18 caracteres).' }

  const fecha = normalizeFecha(row.fecha_nacimiento)
  if (fecha === null) return { valid: false, reason: 'Fecha invalida (use DD/MM/AAAA o AAAA-MM-DD).' }

  const genero = String(row.genero || '').trim()
  if (genero && !GENEROS.includes(genero)) return { valid: false, reason: 'Genero invalido (Varonil o Femenil).' }

  const normalized = {
    nombre_completo: nombre,
    nombres: jugador.nombres,
    apellidos: jugador.apellidos,
    curp,
    fecha_nacimiento: fecha,
    genero,
    numero: String(row.numero || '').trim(),
    nickname: String(row.nickname || '').trim(),
    telefono_jugador: String(row.telefono_jugador || '').trim(),
    tutor_nombre: tutor.nombre,
    tutor_telefono: String(row.tutor_telefono || '').trim(),
    email_tutor: String(row.email_tutor || '').trim(),
    __line: row.__line,
  }
  return { valid: true, normalized }
}

// existing = { curps: Set<curpUpper>, nameDob: Set<`${normalizeName}|${fecha}`> }
export const classifyRows = (rows, existing) => {
  const nuevos = []
  const duplicados = []
  const errores = []
  for (const row of rows) {
    const res = validateRow(row)
    if (!res.valid) { errores.push({ row, reason: res.reason }); continue }
    const n = res.normalized
    const dupByCurp = !!n.curp && existing.curps.has(n.curp)
    const dupByName = !n.curp && !!n.fecha_nacimiento
      && existing.nameDob.has(`${normalizeName(n.nombre_completo)}|${n.fecha_nacimiento}`)
    if (dupByCurp || dupByName) duplicados.push(n)
    else nuevos.push(n)
  }
  return { nuevos, duplicados, errores }
}

// Con que estatus va a quedar una fila de la importacion.
//
// Vive aqui, y no dentro del modal, porque el bug fue justamente que la vista previa y
// la importacion lo calculaban por separado: la tabla prometia "Activo" y createPlayer
// lo degradaba a Pendiente sin decir nada. Una sola funcion, un solo resultado.
//
// Dos reglas, en este orden:
//  1. Si el club exige consentimiento, nadie nace Activo. El consentimiento se pide
//     SOBRE un jugador, asi que uno recien creado nunca puede tenerlo todavia.
//  2. Sin CURP valida tampoco se activa: la CURP gobierna la activacion.
export const estatusDeImportacion = ({ exigeConsentimiento = false, curpValida = false, estatusElegido = 'Activo' } = {}) => {
  if (exigeConsentimiento) return 'Pendiente'
  if (estatusElegido !== 'Activo') return estatusElegido
  return curpValida ? 'Activo' : 'Pendiente'
}
